import React, { useState } from 'react';
import { Logo } from './Logo';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Page } from '../App';
interface HeaderProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
}
const navItems: {
  label: string;
  href: Page;
}[] = [{
  label: 'Home',
  href: 'home'
}, {
  label: 'Service Details',
  href: 'service-details'
}, {
  label: 'About Us',
  href: 'about-us'
}, {
  label: 'Our Service',
  href: 'our-service'
}, {
  label: 'FAQs',
  href: 'faq'
}];
const policyItems: {
  label: string;
  href: Page;
}[] = [{
  label: 'Cancellation Policy',
  href: 'cancellation-policy'
}, {
  label: 'Refund Policy',
  href: 'refund-policy'
}, {
  label: 'Privacy & Cookie Policy',
  href: 'privacy-policy'
}, {
  label: 'Terms & Conditions',
  href: 'terms-conditions'
}];
export function Header({
  currentPage,
  onNavigate
}: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPoliciesOpen, setIsPoliciesOpen] = useState(false);
  const handleNavClick = (href: Page) => {
    onNavigate(href);
    setIsMenuOpen(false);
    setIsPoliciesOpen(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const isPolicyPage = policyItems.some(item => item.href === currentPage);
  return <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-md border-b border-slate-100" data-id="element-54">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-55">
        <div className="flex justify-between items-center h-20" data-id="element-56">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={() => handleNavClick('home')} data-id="element-57">
            <Logo data-id="element-58" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-id="element-59">
            {navItems.map(item => <button key={item.label} onClick={() => handleNavClick(item.href)} className={`text-sm font-medium transition-colors ${currentPage === item.href ? 'text-primary font-semibold' : 'text-slate-600 hover:text-primary'}`} data-id="element-60">
                {item.label}
              </button>)}

            {/* Policies Dropdown */}
            <div className="relative" data-id="element-61">
              <button onClick={() => setIsPoliciesOpen(!isPoliciesOpen)} className={`text-sm font-medium transition-colors flex items-center gap-1 ${isPolicyPage ? 'text-primary font-semibold' : 'text-slate-600 hover:text-primary'}`} data-id="element-62">
                Policies
                <ChevronDown className={`w-4 h-4 transition-transform ${isPoliciesOpen ? 'rotate-180' : ''}`} data-id="element-63" />
              </button>

              <AnimatePresence data-id="element-64">
                {isPoliciesOpen && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-100 py-2" data-id="element-65">
                    {policyItems.map(item => <button key={item.label} onClick={() => handleNavClick(item.href)} className={`w-full text-left px-4 py-2 text-sm transition-colors ${currentPage === item.href ? 'bg-primary/5 text-primary font-semibold' : 'text-slate-600 hover:bg-slate-50 hover:text-primary'}`} data-id="element-66">
                        {item.label}
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            <button onClick={() => handleNavClick('contact-us')} className="bg-accent hover:bg-accent-dark text-white px-6 py-2.5 rounded-full font-medium transition-colors shadow-sm hover:shadow-md active:transform active:scale-95" data-id="element-67">
              Contact Us
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2 text-slate-600 hover:text-primary transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-68">
            {isMenuOpen ? <X className="w-6 h-6" data-id="element-69" /> : <Menu className="w-6 h-6" data-id="element-70" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence data-id="element-71">
        {isMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden bg-white border-b border-slate-100 overflow-hidden" data-id="element-72">
            <div className="px-4 py-4 space-y-4" data-id="element-73">
              {navItems.map(item => <button key={item.label} onClick={() => handleNavClick(item.href)} className={`block w-full text-left text-base font-medium transition-colors py-2 ${currentPage === item.href ? 'text-primary font-semibold' : 'text-slate-600 hover:text-primary'}`} data-id="element-74">
                  {item.label}
                </button>)}

              {/* Mobile Policies Section */}
              <div className="pt-2 border-t border-slate-100" data-id="element-75">
                <button onClick={() => setIsPoliciesOpen(!isPoliciesOpen)} className="flex items-center justify-between w-full text-base font-medium text-slate-600 py-2" data-id="element-76">
                  Policies
                  <ChevronDown className={`w-4 h-4 transition-transform ${isPoliciesOpen ? 'rotate-180' : ''}`} data-id="element-77" />
                </button>

                <AnimatePresence data-id="element-78">
                  {isPoliciesOpen && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="pl-4 space-y-2 overflow-hidden" data-id="element-79">
                      {policyItems.map(item => <button key={item.label} onClick={() => handleNavClick(item.href)} className={`block w-full text-left text-sm py-2 ${currentPage === item.href ? 'text-primary font-semibold' : 'text-slate-500 hover:text-primary'}`} data-id="element-80">
                          {item.label}
                        </button>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <div className="pt-4 border-t border-slate-100" data-id="element-81">
                <button onClick={() => handleNavClick('contact-us')} className="w-full bg-accent hover:bg-accent-dark text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm" data-id="element-82">
                  Contact Us
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}